import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { AverageReportType, GroupType, HighlightedProps, SchoolReportType } from '../../types';
import { SectionLayout } from '../modules';
import type { ChartIconType } from '../../../components/charts/types';
import { StudentProgress } from '../../../components';

type Props = {
  schoolReport: SchoolReportType
  showOthersData: boolean
  iconToUse: ChartIconType
} & HighlightedProps;

const InitialAssessmentCompleted = ({ schoolReport, showOthersData, iconToUse, highlightedLabel, highlightedIds, onElementClick }: Props): JSX.Element => {
  const { translate } = useI18n();

  const dataToUse = (): (GroupType & AverageReportType)[] => {
    if (iconToUse === 'class') {
      return schoolReport.classes;
    } else return schoolReport.yearGroups;
  };

  const completed = dataToUse().map((gr) => gr.assessedStudents).reduce((a, b) => a + b, 0);
  const totalStudents = dataToUse().map((gr) => gr.totalStudentsNum).reduce((a, b) => a + b, 0);

  const getHighlightedValue = (): string | null => {
    if (highlightedIds && highlightedIds.length > 0) {
      const sumHighlightedAssessed = dataToUse()
        .filter((gr) => highlightedIds.includes(gr.id))
        .map((gr) => gr.assessedStudents)
        .reduce((a, b) => a + b, 0);
      const sumHighlightedTotal = dataToUse()
        .filter((gr) => highlightedIds.includes(gr.id))
        .map((gr) => gr.totalStudentsNum)
        .reduce((a, b) => a + b, 0);
      return `${sumHighlightedAssessed} / ${sumHighlightedTotal}`;
    }

    return null;
  };

  const value = `${completed} / ${totalStudents}`;

  return (
    <SectionLayout
      title={translate('pages.SCHOOL_REPORTS.SECTIONS.INITIAL_ASSESSMENT.title')}
      value={showOthersData ? value : null}
      highlightLabel={highlightedLabel}
      highlightValue={getHighlightedValue()}
      unit={translate('pages.SCHOOL_REPORTS.SECTIONS.INITIAL_ASSESSMENT.unit', { count: completed })}
      moreInfo={translate('pages.SCHOOL_REPORTS.SECTIONS.INITIAL_ASSESSMENT.info')}
      className={'mb-md'}
      id={'initial-assessment-completed-container'}
    >
      <div>
        <StudentProgress
          showOthersData={showOthersData}
          data={dataToUse()}
          chartType={'initialAssessment'}
          type={iconToUse}
          highlightedIds={highlightedIds}
          thinAxis
          onElementClick={onElementClick}
        />
      </div>
    </SectionLayout>
  );
};

InitialAssessmentCompleted.displayName = 'InitialAssessmentCompleted';

export default InitialAssessmentCompleted;
